import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  MenuList,
  MenuItem,
  Paper,
  Popper as MUIPopper,
  TextField,
} from '@mui/material';
import { Close } from '@mui/icons-material';

import FormContext from '../../HO/State/StateContext';
import TranslationsContext from '../../Container/Language/TranslationsContext';
// import { ManagerUsersConsumer, ManagerUsersProvider } from '../../Container/manage/User';

const formatManagerLabel = (m) => {
  if (!m) return '';
  const id = `#${m.id ?? ''}${m.site_id ? `:${m.site_id}` : ''}`;
  const name = [m.firstName, m.lastName].filter(Boolean).join(' ').trim();
  if (name && m.username) return `${name} (${m.username}) ${id}`;
  if (m.username && m.company) return `${m.username} / ${m.company} ${id}`;
  if (m.username) return `${m.username} ${id}`;
  if (name) return `${name} ${id}`;
  return id;
};

const ManagerSearchField = (props) => {
  const { sendRequest } = useContext(FormContext);
  const { t } = useContext(TranslationsContext);

  const {
    value,
    onChange,
    // setForm,
    label,
    placeholder,
    fullWidth,
    sx,
    error,
    disabled,
    InputProps: inputPropsProp,
    byManagers,
    // data,
  } = props;
  const timer = useRef(null);
  const [inputValue, setInputValue] = useState(Number(value) > 0 ? Number(value) : '');
  const [loading, setLoading] = useState(false);
  const [found, setFound] = useState(null);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const textFieldRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      if (!Number(value)) {
        return;
      }
    }

    setLoading(true);

    sendRequest('managerUsers', {
      body: {
        byManagers: byManagers !== false,
        filterValue: String(value),
        orderBy: 'name',
        perPage: 10,
      },
    }).then((response) => {
      setLoading(false);
      const list = response.data.customers || [];
      if (!list.length) return;

      const manager = list.find((m) => String(m.id) === String(value)) || list[0];
      setFound(manager);
      setInputValue(formatManagerLabel(manager));
      setResults(list);
    }).catch(() => {
      setLoading(false);
    });
  }, [sendRequest, value, byManagers]);

  const handleInputChange = (e) => {
    const v = e.target.value;

    setResults([]);
    setInputValue(v);
    clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      setLoading(true);

      sendRequest('managerUsers', {
        body: {
          byManagers: byManagers !== false,
          filterValue: v,
          orderBy: 'name',
          perPage: 10,
        },
      }).then((response) => {
        setLoading(false);
        setResults(response.data.customers || []);
      });
    }, 1000);
  };

  const handleSelect = (manager) => {
    if (onChange) {
      onChange(manager ? manager.id : null);
    }
    setInputValue(manager ? formatManagerLabel(manager) : '');
    setOpen(false);
    setFound(manager);
  };

  const textFieldProps = {
    fullWidth: fullWidth !== false,
    label,
    placeholder: placeholder || null,
    value: found ? formatManagerLabel(found) : inputValue,
    onChange: handleInputChange,
    onFocus: () => setOpen(true),
    sx: {
      '& .MuiOutlinedInput-root': {
        bgcolor: 'background.paper',
      },
      ...sx,
    },
    error: !!error,
    disabled: !!disabled,
    InputProps: {
      ...inputPropsProp,
      endAdornment: loading ? (
        <InputAdornment position="end">
          <CircularProgress size={24} />
        </InputAdornment>
      ) : (found ? (
        <InputAdornment position="end">
          <IconButton onClick={() => handleSelect(null)}>
            <Close />
          </IconButton>
        </InputAdornment>
      ) : inputPropsProp?.endAdornment),
    },
    ref: textFieldRef,
  };

  // useEffect(() => {
  //   if (data && loading) {
  //     setLoading(false);
  //     setResults(data.customers);
  //   }
  // }, [data, loading]);

  return (
    <>
      <Box ref={anchorRef} sx={{ width: '100%' }}>
        <TextField {...textFieldProps} />
      </Box>

      <MUIPopper open={open && results.length > 0} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1300 }}>
        <Paper elevation={8} sx={{ minWidth: anchorRef.current ? anchorRef.current.offsetWidth : 280, maxHeight: 320, overflow: 'auto' }}>
          <ClickAwayListener
            onClickAway={(e) => !textFieldRef.current.contains(e.target) && setOpen(false)}
          >
            <MenuList sx={{ p: 0, m: 0 }}>
              {results.length === 0 && (
                <MenuItem>{t('app:common:empty')}</MenuItem>
              )}
              {results.map((m) => (
                <MenuItem
                  key={m.id}
                  onClick={() => handleSelect(m)}
                  sx={{ whiteSpace: 'normal' }}
                >
                  {formatManagerLabel(m) || m.id}
                </MenuItem>
              ))}
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </MUIPopper>
    </>
  );
};

ManagerSearchField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  form: PropTypes.shape({}),
  setForm: PropTypes.func,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  fullWidth: PropTypes.bool,
  sx: PropTypes.shape({}),
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  InputProps: PropTypes.shape({}),
  onSearch: PropTypes.func,
  byManagers: PropTypes.bool,
};

ManagerSearchField.defaultProps = {
  value: '',
  onChange: null,
  form: null,
  setForm: null,
  name: '',
  label: '',
  placeholder: '',
  fullWidth: true,
  sx: {},
  error: false,
  disabled: false,
  InputProps: undefined,
  onSearch: null,
  byManagers: true,
};

// const ManagerSearchFieldContext = (props) => (
//   <ManagerUsersProvider form={null}>
//     <ManagerUsersConsumer>
//       {(value) => (
//         <ManagerSearchField {...props} {...value} />
//       )}
//     </ManagerUsersConsumer>
//   </ManagerUsersProvider>
// );

export default ManagerSearchField;
